import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Trash2, Send } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import Avatar from '../common/Avatar';
import { getRelativeTime } from '../../utils/formatting';

const PHOTO_DURATION = 5000; // ms for photos and text swings

/**
 * Full-screen Swing (story) viewer.
 * - Progress bars at top
 * - Tap left/right to navigate swings within a user
 * - Swipe left/right to jump between users
 * - Auto-advances: 5s for photo/text, video plays to end
 * - Reply input at bottom sends a silent DM
 */
const SwingViewer = ({
  userOrder,      // string[] — ordered userId list
  initialUserId,
  swings,         // all active swings
  currentUser,
  users,
  onClose,
  onReply,        // (swingOwnerId, message) => void
  onDelete,       // (swingId) => void
}) => {
  const [userIdx, setUserIdx] = useState(() => Math.max(0, userOrder.indexOf(initialUserId)));
  const [swingIdx, setSwingIdx] = useState(0);
  const [reply, setReply] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const currentUserId = userOrder[userIdx];
  const viewedUser = users.find(u => u.id === currentUserId);

  // Swings for current user, sorted oldest-first so they play in chronological order
  const userSwings = swings
    .filter(s => s.userId === currentUserId)
    .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));

  const currentSwing = userSwings[swingIdx] || null;
  const isOwnSwing = currentUserId === currentUser.id;

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    setSwingIdx(prev => {
      if (prev < userSwings.length - 1) return prev + 1;
      // Move to next user
      setUserIdx(uIdx => {
        if (uIdx < userOrder.length - 1) {
          setSwingIdx(0);
          return uIdx + 1;
        }
        onClose();
        return uIdx;
      });
      return 0;
    });
  }, [userSwings.length, userOrder.length, onClose]);

  const goPrev = useCallback(() => {
    setSwingIdx(prev => {
      if (prev > 0) return prev - 1;
      // Move to previous user
      setUserIdx(uIdx => {
        if (uIdx > 0) {
          setSwingIdx(0);
          return uIdx - 1;
        }
        return uIdx;
      });
      return 0;
    });
  }, []);

  const goNextUser = useCallback(() => {
    setUserIdx(prev => {
      if (prev < userOrder.length - 1) {
        setSwingIdx(0);
        return prev + 1;
      }
      onClose();
      return prev;
    });
  }, [userOrder.length, onClose]);

  const goPrevUser = useCallback(() => {
    setUserIdx(prev => {
      if (prev > 0) {
        setSwingIdx(0);
        return prev - 1;
      }
      return prev;
    });
  }, []);

  // ── Mark viewed ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentSwing) return;
    if (!(currentSwing.viewedBy || []).includes(currentUser.id)) {
      firestoreService.markSwingViewed(currentSwing.id, currentUser.id).catch(() => {});
    }
  }, [currentSwing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-advance timer for photos / text ──────────────────────────────────
  useEffect(() => {
    clearTimeout(timerRef.current);
    setVideoProgress(0);
    setVideoDuration(0);
    if (!currentSwing) { goNext(); return; }
    if (currentSwing.type === 'video') return; // video handles its own advancement
    timerRef.current = setTimeout(() => goNext(), PHOTO_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [swingIdx, userIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset reply sent state when user changes
  useEffect(() => {
    setReply('');
    setReplySent(false);
  }, [userIdx]);

  // ── Touch / tap handlers ──────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const dx = touchStartXRef.current - e.changedTouches[0].clientX;
    const dy = touchStartYRef.current - e.changedTouches[0].clientY;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    // Swipe: horizontal > 60px AND more horizontal than vertical → navigate users
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goNextUser();
      else goPrevUser();
    }
  };

  const handleMediaTap = (e) => {
    const x = e.clientX;
    const w = e.currentTarget.offsetWidth;
    if (x < w * 0.35) goPrev();
    else goNext();
  };

  // ── Video events ──────────────────────────────────────────────────────────
  const handleVideoTimeUpdate = (e) => {
    const { currentTime, duration } = e.target;
    if (duration) setVideoProgress((currentTime / duration) * 100);
  };
  const handleVideoLoadedMetadata = (e) => {
    setVideoDuration(e.target.duration || 0);
  };
  const handleVideoEnded = () => goNext();

  // ── Reply ─────────────────────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!reply.trim() || replySent) return;
    await onReply(currentUserId, reply.trim());
    setReply('');
    setReplySent(true);
    setTimeout(() => setReplySent(false), 2000);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!currentSwing) return;
    onDelete(currentSwing.id);
    if (userSwings.length <= 1) {
      goNextUser();
    } else {
      goNext();
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const progressDuration = currentSwing?.type === 'video' && videoDuration
    ? videoDuration * 1000
    : PHOTO_DURATION;

  // If userSwings is empty, close
  if (!currentUserId || userSwings.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Progress bars + header ── */}
      <div className="absolute top-0 inset-x-0 z-10 px-2 pt-2 pb-1 pointer-events-none">
        {/* Progress bars */}
        <div className="flex space-x-1 mb-2">
          {userSwings.map((s, i) => (
            <div key={s.id} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
              {i < swingIdx ? (
                <div className="h-full w-full bg-white" />
              ) : i === swingIdx ? (
                s.type === 'video' ? (
                  <div className="h-full bg-white" style={{ width: `${videoProgress}%` }} />
                ) : (
                  <div
                    key={`prog-${userIdx}-${swingIdx}`}
                    className="h-full w-full bg-white"
                    style={{
                      transformOrigin: 'left',
                      animation: `swingProgress ${progressDuration}ms linear forwards`,
                    }}
                  />
                )
              ) : (
                <div className="h-full w-0 bg-white" />
              )}
            </div>
          ))}
        </div>

        {/* User info row */}
        <div className="flex items-center justify-between pointer-events-auto px-2">
          <div className="flex items-center space-x-2">
            {viewedUser && (
              <Avatar user={viewedUser} size="xs" />
            )}
            <span className="text-pure-white text-sm font-bold">{viewedUser?.handle || ''}</span>
            {currentSwing && (
              <span className="text-white/60 text-xs">{getRelativeTime(currentSwing.createdAt)}</span>
            )}
          </div>
          <button onClick={onClose} className="text-pure-white p-1">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* ── Media area (tappable) ── */}
      <div className="flex-1 relative" onClick={handleMediaTap}>
        {currentSwing?.type === 'image' && (
          <img
            src={currentSwing.url}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
        )}
        {currentSwing?.type === 'video' && (
          <video
            ref={videoRef}
            key={currentSwing.id}
            src={currentSwing.url}
            className="absolute inset-0 w-full h-full object-contain"
            autoPlay
            playsInline
            onTimeUpdate={handleVideoTimeUpdate}
            onLoadedMetadata={handleVideoLoadedMetadata}
            onEnded={handleVideoEnded}
          />
        )}
        {currentSwing?.type === 'text' && (
          <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${currentSwing.bgGradient}`}>
            <p className="text-pure-white text-2xl font-bold text-center leading-relaxed px-8 break-words max-w-sm">
              {currentSwing.text}
            </p>
          </div>
        )}
      </div>

      {/* ── Bottom bar ── */}
      <div
        className="absolute bottom-0 inset-x-0 z-10 pb-safe"
        onClick={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
      >
        {isOwnSwing ? (
          /* Own swing: show delete button */
          <div className="flex items-center justify-between px-4 py-4">
            <div />
            <button
              onClick={handleDelete}
              className="flex items-center space-x-1.5 text-white/70 hover:text-red-400"
            >
              <Trash2 className="w-5 h-5" />
              <span className="text-sm">Delete</span>
            </button>
          </div>
        ) : (
          /* Others' swing: show reply input */
          <div className="px-4 py-3 flex items-center space-x-2">
            {replySent ? (
              <p className="text-pure-white/70 text-sm text-center w-full">Reply sent</p>
            ) : (
              <>
                <input
                  type="text"
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleSendReply()}
                  placeholder={`Reply to ${viewedUser?.handle || 'user'}…`}
                  className="flex-1 bg-transparent border border-white/40 rounded-full px-4 py-2 text-sm text-pure-white placeholder-white/40 outline-none focus:border-white/70"
                />
                {reply.trim() && (
                  <button onClick={handleSendReply} className="text-pure-white">
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* CSS animation for progress bar */}
      <style>{`
        @keyframes swingProgress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
};

export default SwingViewer;
